import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useMatches, useParams } from 'react-router';

import SideBarContainer, { SideBarPaddingWrapper } from 'DesignSystem/features/sidebar';
import { Skeleton } from 'DesignSystem/ui/skeleton';
import { Tabs, TabsContent } from 'DesignSystem/ui/tabs';
import { cn } from 'DesignSystem/utils';
import { prefCardItemsQuery } from 'pages/preference-cards/api';
import { prefCardPackContentsQuery } from 'pages/preference-cards/api/pack-contents';
import { usePrefCardProcedureConflictsQuery } from 'pages/preference-cards/api/preference-card-conflicts';
import type { PrefCardsItem } from 'pages/preference-cards/types';
import { useCustomPacksEnabled } from 'pages/preference-cards/use-custom-packs-enabled';

import { useItemSidebarRouteState } from '../../item-sidebar-route-state';
import type { ItemSidebarRouteState } from '../../item-sidebar-route-state';
import { canFetchPrefCardItemSummary } from '../../items-table/items-filter-utils';
import SideBarAction from './actions';
import { ItemCharts } from './item-charts';
import { ItemHeader } from './item-header';
import { ItemSidebarTabs } from './item-sidebar-tabs';
import { ItemSummary } from './item-summary';
import { ModifyQuantity } from './modify-quantity';
import { PackContents } from './pack-contents';

type Props = { itemId: string };
export function SelectedItem({ itemId }: Props) {
  const matches = useMatches();
  const routeState = useItemSidebarRouteState();
  const { setTab, tab } = routeState;
  const isItemsRoute = matches.some((match) => match.id === 'co-card-items');
  const isStandardizationRoute = matches.some((match) => match.id === 'co-card-standardization');
  const routeType = isItemsRoute ? 'items' : isStandardizationRoute ? 'standardization' : null;

  React.useEffect(() => {
    if (routeType === 'standardization' && tab === 'pack_contents') {
      setTab('overview', true);
    }
  }, [routeType, setTab, tab]);

  return (
    <SideBarContainer className="h-full max-h-full min-h-0 overflow-y-hidden">
      {routeType === 'items' ? <ItemsSidebar itemId={itemId} routeState={routeState} /> : null}
      {routeType === 'standardization' ? <StandardizationSidebar itemId={itemId} routeState={routeState} /> : null}
    </SideBarContainer>
  );
}

function ItemsSidebar(props: { itemId: string; routeState: ItemSidebarRouteState }) {
  const { cardId } = useParams<'cardId'>();
  const { closeItem, selectedItemId, setTab, tab } = props.routeState;
  const customPacksEnabled = useCustomPacksEnabled();
  const { data: item, isSuccess } = useQuery({
    ...prefCardItemsQuery(cardId!),
    select(data) {
      return data.find((item) => item.preference_card_client_item_master_id === props.itemId);
    },
    enabled: !!props.itemId,
  });
  const isPack = customPacksEnabled && item?.is_pack === true;
  const packClientItemMasterId = isPack && item.client_item_master_id ? item.client_item_master_id : null;
  const canShowPackContents = packClientItemMasterId !== null;
  const activeTab = canShowPackContents ? tab : 'overview';
  const { data: packComponents, isLoading: isPackContentsLoading } = useQuery(
    prefCardPackContentsQuery(packClientItemMasterId)
  );

  React.useEffect(() => {
    if (isSuccess && item && props.itemId === selectedItemId && !canShowPackContents && tab === 'pack_contents') {
      setTab('overview', true);
    }
  }, [canShowPackContents, isSuccess, item, props.itemId, selectedItemId, setTab, tab]);

  function handleTabChange(tab: string) {
    if (tab === 'overview' || tab === 'pack_contents') {
      setTab(tab);
    }
  }

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="flex min-h-0 flex-1 flex-col">
      <div
        className={cn(
          'sticky top-0 z-10 w-full shrink-0 overflow-hidden border-b border-zinc-200',
          isPack ? 'bg-white' : 'bg-background'
        )}
      >
        <div className="px-3 pb-3 pt-4">
          {isSuccess && item ? (
            <ItemHeader
              clientItemMasterId={item.client_item_master_id ?? ''}
              description={item.client_item_description}
              mfgId={item.client_erp_item_id}
              mfgCatalogId={item.client_mfg_catalog_id}
              isActive={item.is_active}
              inActiveChangeRequest={item.in_active_change_request}
              hasRecommendations={
                !item.in_active_change_request && !item.snooze_until && item.recommended_action_display_name !== null
              }
              snoozeUntil={item.snooze_until}
              uimLongDescription={item.uim_long_description}
              onClose={closeItem}
              compact={isPack}
            />
          ) : (
            <Skeleton className="h-[83px] w-full" />
          )}
        </div>
        {canShowPackContents ? <ItemSidebarTabs /> : null}
      </div>
      <SideBarPaddingWrapper
        classNames={cn('min-h-0 flex-1', activeTab === 'pack_contents' ? 'flex flex-col py-0' : 'overflow-y-auto')}
      >
        <TabsContent value="overview" className="mt-0">
          <ItemsSidebarOverview
            item={item}
            itemId={props.itemId}
            cardId={cardId!}
            isSuccess={isSuccess}
            isPack={isPack}
            packComponentCount={packComponents?.length ?? null}
          />
        </TabsContent>
        {canShowPackContents ? (
          <TabsContent value="pack_contents" className="mt-0 flex min-h-0 flex-1 flex-col">
            <PackContents components={packComponents} isLoading={isPackContentsLoading} />
          </TabsContent>
        ) : null}
      </SideBarPaddingWrapper>
    </Tabs>
  );
}

function ItemsSidebarOverview(props: {
  item: PrefCardsItem | undefined;
  itemId: string;
  cardId: string;
  isSuccess: boolean;
  isPack: boolean;
  packComponentCount: number | null;
}) {
  const canFetchItemDetails = props.item != null && canFetchPrefCardItemSummary(props.item);

  return (
    <div className="flex flex-col gap-y-3">
      {props.isSuccess && props.item ? (
        <SideBarAction
          item={props.item}
          key={props.item.client_erp_item_id}
          variant={props.isPack ? 'pack' : 'default'}
        />
      ) : (
        <div>
          <Skeleton className="h-[224px] w-full" />
        </div>
      )}
      <ItemSummary
        itemId={props.itemId}
        cardId={props.cardId}
        itemName={props.item?.client_item_description || ''}
        mfgName={props.item?.client_mfg_name || null}
        mfgCatalogId={props.item?.client_mfg_catalog_id || null}
        isPack={props.isPack || undefined}
        packComponentCount={props.isPack ? props.packComponentCount : undefined}
        canFetchItemDetails={canFetchItemDetails}
      />
      {canFetchItemDetails ? <ItemCharts cardId={props.cardId} itemId={props.itemId} /> : null}
    </div>
  );
}

function StandardizationSidebar(props: { itemId: string; routeState: ItemSidebarRouteState }) {
  const { cardId } = useParams<'cardId'>();
  const { data, isSuccess } = usePrefCardProcedureConflictsQuery(cardId!, (data) =>
    data.filter((item) => item.pccim_id === props.itemId)
  );
  const item = data?.[0];
  const shouldRenderCharts = item ? item.difference_type !== 'ADD' : false;

  return (
    <React.Fragment>
      <div className="sticky top-0 w-full shrink-0 border-b border-zinc-200 px-3 pb-3 pt-4">
        {isSuccess && item ? (
          <ItemHeader
            clientItemMasterId={item.cim_id ?? ''}
            description={item.description}
            mfgId={item.erp_item_id}
            mfgCatalogId={item.manufacturer_catalog_id}
            isActive={false}
            inActiveChangeRequest={Boolean(item.preference_card_change_request_id)}
            hasRecommendations={false}
            snoozeUntil={null}
            uimLongDescription={item.uim_long_description}
            onClose={props.routeState.closeItem}
          />
        ) : (
          <Skeleton className="h-[83px] w-full" />
        )}
      </div>
      <SideBarPaddingWrapper classNames="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col gap-3">
          <ModifyQuantity cardId={cardId!} itemId={props.itemId} />

          <ItemSummary
            itemId={props.itemId}
            cardId={cardId!}
            itemName={item?.description || ''}
            mfgName={item?.manufacturer_name ?? null}
            mfgCatalogId={item?.manufacturer_catalog_id ?? null}
            differenceType={item?.difference_type}
          />
          {shouldRenderCharts ? <ItemCharts cardId={cardId!} itemId={props.itemId} /> : null}
        </div>
      </SideBarPaddingWrapper>
    </React.Fragment>
  );
}
